import { useEffect, useState } from 'react';
import {
  Button,
  Spin,
  Empty,
  Descriptions,
  List,
  Tag,
  Space,
  Popconfirm,
  Input,
  message,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { getVersion, renameVersion } from '../requests/versions';
import { useStore } from '../store/store';
import { diffContent } from './contentDiff';
import { summarizeDiff } from './diffSummary';
import { reasonLabel } from './reasons';
import { formatDate } from './format';

// Détail d'une version : métadonnées + résumé des différences avec l'ÉTAT ACTUEL
// du document (currentContent, même format stocké que la version → diffContent
// fiable). Une version identique à l'état courant (ex. celle qu'on vient de
// restaurer) n'affiche aucune différence.
export const VersionDetail = ({
  documentType,
  documentId,
  version,
  currentContent,
  onBack,
  onRestored,
  onRenamed,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lines, setLines] = useState([]);
  const [restoring, setRestoring] = useState(false);
  const [label, setLabel] = useState(version.label ?? '');
  const [savingLabel, setSavingLabel] = useState(false);
  const restoreContentVersion = useStore((state) => state.restoreContentVersion);

  const handleRename = async () => {
    const next = label.trim();
    if (next === (version.label ?? '')) return;
    setSavingLabel(true);
    try {
      await renameVersion({
        documentType,
        documentId,
        versionId: version.id,
        label: next,
      });
      message.success('Nom enregistré');
      onRenamed?.(version.id, next);
    } catch (err) {
      console.error('Renommage échoué', err);
      message.error({
        content: 'Échec du renommage',
        key: 'version-rename-error',
      });
    } finally {
      setSavingLabel(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const refusal = await restoreContentVersion(version.id);
      // Refus (co-éditeur en train d'éditer) : même contrat que moveBoxesIndex,
      // l'action retourne le message à afficher.
      if (typeof refusal === 'string') {
        message.warning(refusal);
        return;
      }
      message.success('Version restaurée');
      // Recharge l'historique (la restauration a créé une version before_restore)
      // et revient à la liste, drawer ouvert, pour refléter le nouvel état.
      onRestored?.();
    } catch (err) {
      console.error('Restauration échouée', err);
      message.error({
        content: 'Échec de la restauration',
        key: 'version-restore-error',
      });
    } finally {
      setRestoring(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(false);
      try {
        const { content } = await getVersion({
          documentType,
          documentId,
          versionId: version.id,
        });
        // Différences entre l'état actuel et cette version = ce que la
        // restauration changerait. Identique → aucune ligne.
        const diff = diffContent(currentContent, content);
        if (!cancelled) setLines(summarizeDiff(diff));
      } catch (err) {
        console.error('Chargement du détail de version échoué', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [documentType, documentId, version.id, currentContent]);

  return (
    <>
      <Button
        type='link'
        onClick={onBack}
        icon={
          <FontAwesomeIcon
            icon={icon({ name: 'arrow-left', style: 'solid' })}
            style={{ marginRight: 6 }}
          />
        }
        style={{ paddingLeft: 0, marginBottom: 8 }}
      >
        Retour à la liste
      </Button>

      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 4, color: '#888' }}>Nom de la version</div>
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={handleRename}
          onPressEnter={(e) => e.target.blur()}
          placeholder='Nommer cette version'
          maxLength={120}
          disabled={savingLabel}
        />
      </div>

      <Descriptions column={1} size='small' style={{ marginBottom: 16 }}>
        <Descriptions.Item label='Type'>
          {reasonLabel(version.reason)}
        </Descriptions.Item>
        <Descriptions.Item label='Date'>
          {formatDate(version.created_at)}
        </Descriptions.Item>
        {version.author_name ? (
          <Descriptions.Item label='Auteur'>
            {version.author_name}
          </Descriptions.Item>
        ) : null}
      </Descriptions>

      <Popconfirm
        title='Restaurer cette version ?'
        description='Le contenu actuel du document sera remplacé par cette version.'
        okText='Restaurer'
        cancelText='Annuler'
        onConfirm={handleRestore}
      >
        <Button type='primary' loading={restoring} style={{ marginBottom: 16 }}>
          Restaurer cette version
        </Button>
      </Popconfirm>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : error ? (
        <Empty description='Erreur de chargement du diff' />
      ) : lines.length === 0 ? (
        <Empty description='Identique à l’état actuel du document' />
      ) : (
        <List
          size='small'
          header={<strong>Différences avec l’état actuel</strong>}
          dataSource={lines}
          rowKey='key'
          renderItem={(line) => (
            <List.Item>
              <Space>
                <span>{line.label}</span>
                {line.changed ? (
                  <Tag color='orange'>modifiée</Tag>
                ) : (
                  <>
                    {line.added ? (
                      <Tag color='green'>+{line.added}</Tag>
                    ) : null}
                    {line.modified ? (
                      <Tag color='orange'>~{line.modified}</Tag>
                    ) : null}
                    {line.removed ? (
                      <Tag color='red'>−{line.removed}</Tag>
                    ) : null}
                  </>
                )}
              </Space>
            </List.Item>
          )}
        />
      )}
    </>
  );
};

export default VersionDetail;
